import React, { useEffect, useMemo, useState } from "react";
import { api, fmtSDG } from "@/lib/api";
import { useApp } from "@/contexts/AppContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Download, TrendingUp, ShoppingBag, Receipt, Trash2, Tag, Calendar } from "lucide-react";

function toISODate(d) {
    return d.toISOString().slice(0, 10);
}
function todayISO() {
    return toISODate(new Date());
}
function isoMinusDays(n) {
    const d = new Date();
    d.setDate(d.getDate() - n);
    return toISODate(d);
}

export default function Reports() {
    const { t, lang } = useApp();
    const [mode, setMode] = useState("range"); // 'range' | 'single'
    const [dateFrom, setDateFrom] = useState(isoMinusDays(13));
    const [dateTo, setDateTo] = useState(todayISO());
    const [singleDate, setSingleDate] = useState(todayISO());
    const [data, setData] = useState(null);
    const [audit, setAudit] = useState(null);

    const effective = useMemo(() => {
        if (mode === "single") return { date_from: singleDate, date_to: singleDate };
        return { date_from: dateFrom, date_to: dateTo };
    }, [mode, dateFrom, dateTo, singleDate]);

    useEffect(() => {
        api.get("/dashboard/daily-report", { params: effective }).then((r) => setData(r.data));
        api.get("/dashboard/audit-log", { params: { ...effective, limit: 200 } }).then((r) => setAudit(r.data));
    }, [effective]);

    const fmtDate = (iso) =>
        new Date(iso + (iso.length === 10 ? "T00:00:00" : "")).toLocaleDateString(
            lang === "ar" ? "ar-SD" : "en-US",
            { weekday: "short", year: "numeric", month: "short", day: "numeric" }
        );
    const fmtTime = (iso) =>
        new Date(iso).toLocaleString(lang === "ar" ? "ar-SD" : "en-US", {
            month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
        });

    const exportCsv = () => {
        if (!data) return;
        const lines = ["Date,Orders,Revenue (SDG),Discount,Item,Qty,Item Revenue"];
        for (const d of data.days) {
            if (d.items.length === 0) {
                lines.push(`${d.date},${d.orders},${d.total},${d.discount || 0},,,`);
            } else {
                d.items.forEach((it, i) => {
                    lines.push(`${i === 0 ? d.date : ""},${i === 0 ? d.orders : ""},${i === 0 ? d.total : ""},${i === 0 ? (d.discount || 0) : ""},"${it.name_en}",${it.qty},${it.revenue}`);
                });
            }
        }
        const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `cafe24-report-${effective.date_from}_to_${effective.date_to}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    if (!data) return <div className="text-muted-foreground">{t("loading")}</div>;

    const totals = data.days.reduce(
        (a, d) => ({
            revenue: a.revenue + d.total,
            orders: a.orders + d.orders,
            discount: a.discount + (d.discount || 0),
        }),
        { revenue: 0, orders: 0, discount: 0 }
    );
    const totalItems = data.days.reduce((a, d) => a + d.items.reduce((s, it) => s + it.qty, 0), 0);

    return (
        <div className="space-y-5" data-testid="reports-page">
            <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                    <div className="text-sm text-muted-foreground">{t("reports")}</div>
                    <h1 className={`${lang === "ar" ? "font-ar" : "font-display-en italic"} text-3xl`}>{t("daily_report")}</h1>
                </div>
                <Button variant="outline" onClick={exportCsv} data-testid="export-csv-btn">
                    <Download className="h-4 w-4 me-2" />{t("export_csv")}
                </Button>
            </div>

            {/* Date range picker */}
            <Card className="grain bg-card/70">
                <CardContent className="p-4 flex flex-wrap items-end gap-3">
                    <div className="flex items-center bg-secondary/50 rounded-xl p-1 gap-1">
                        <button
                            onClick={() => setMode("range")}
                            className={`px-4 py-1.5 rounded-lg text-sm transition-all ${mode === "range" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}
                            data-testid="mode-range"
                        >
                            {t("date_range")}
                        </button>
                        <button
                            onClick={() => setMode("single")}
                            className={`px-4 py-1.5 rounded-lg text-sm transition-all ${mode === "single" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}
                            data-testid="mode-single"
                        >
                            {t("single_day")}
                        </button>
                    </div>
                    {mode === "range" ? (
                        <>
                            <div className="space-y-1">
                                <Label className="text-xs flex items-center gap-1"><Calendar className="h-3 w-3" />{t("from_date")}</Label>
                                <Input type="date" value={dateFrom} max={dateTo} onChange={(e) => setDateFrom(e.target.value)} className="w-44" data-testid="date-from" />
                            </div>
                            <div className="space-y-1">
                                <Label className="text-xs flex items-center gap-1"><Calendar className="h-3 w-3" />{t("to_date")}</Label>
                                <Input type="date" value={dateTo} min={dateFrom} max={todayISO()} onChange={(e) => setDateTo(e.target.value)} className="w-44" data-testid="date-to" />
                            </div>
                            <div className="flex gap-1.5">
                                <Button variant="ghost" size="sm" onClick={() => { setDateFrom(todayISO()); setDateTo(todayISO()); }} data-testid="qs-today">{t("today")}</Button>
                                <Button variant="ghost" size="sm" onClick={() => { setDateFrom(isoMinusDays(6)); setDateTo(todayISO()); }} data-testid="qs-7d">{t("last_7_days")}</Button>
                                <Button variant="ghost" size="sm" onClick={() => { setDateFrom(isoMinusDays(29)); setDateTo(todayISO()); }} data-testid="qs-30d">{t("last_30_days")}</Button>
                            </div>
                        </>
                    ) : (
                        <div className="space-y-1">
                            <Label className="text-xs flex items-center gap-1"><Calendar className="h-3 w-3" />{t("select_date")}</Label>
                            <Input type="date" value={singleDate} max={todayISO()} onChange={(e) => setSingleDate(e.target.value)} className="w-44" data-testid="date-single" />
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* KPI cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Card className="grain bg-card/70" data-testid="report-kpi-revenue">
                    <CardContent className="p-4 flex items-center gap-3">
                        <div className="rounded-xl bg-primary/15 text-primary p-2.5"><TrendingUp className="h-5 w-5" /></div>
                        <div>
                            <div className="text-xs text-muted-foreground">{t("revenue")}</div>
                            <div className="text-xl font-medium tabular-nums">{fmtSDG(totals.revenue, lang)}</div>
                        </div>
                    </CardContent>
                </Card>
                <Card className="grain bg-card/70" data-testid="report-kpi-orders">
                    <CardContent className="p-4 flex items-center gap-3">
                        <div className="rounded-xl bg-primary/15 text-primary p-2.5"><Receipt className="h-5 w-5" /></div>
                        <div>
                            <div className="text-xs text-muted-foreground">{t("orders_count")}</div>
                            <div className="text-xl font-medium tabular-nums">{totals.orders}</div>
                        </div>
                    </CardContent>
                </Card>
                <Card className="grain bg-card/70" data-testid="report-kpi-items">
                    <CardContent className="p-4 flex items-center gap-3">
                        <div className="rounded-xl bg-primary/15 text-primary p-2.5"><ShoppingBag className="h-5 w-5" /></div>
                        <div>
                            <div className="text-xs text-muted-foreground">{t("items")}</div>
                            <div className="text-xl font-medium tabular-nums">{totalItems}</div>
                        </div>
                    </CardContent>
                </Card>
                <Card className="grain bg-card/70" data-testid="report-kpi-discount">
                    <CardContent className="p-4 flex items-center gap-3">
                        <div className="rounded-xl bg-emerald-500/15 text-emerald-400 p-2.5"><Tag className="h-5 w-5" /></div>
                        <div>
                            <div className="text-xs text-muted-foreground">{t("discount")}</div>
                            <div className="text-xl font-medium tabular-nums text-emerald-400">− {fmtSDG(totals.discount, lang)}</div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Daily breakdown */}
            {data.days.length === 0 ? (
                <div className="text-center py-16 text-muted-foreground" data-testid="no-data">{t("no_data")}</div>
            ) : (
                <div className="space-y-4">
                    {data.days.map((d) => (
                        <Card key={d.date} className="grain bg-card/70" data-testid={`day-report-${d.date}`}>
                            <CardContent className="p-5">
                                <div className="flex flex-wrap items-center justify-between gap-3 mb-3 pb-3 border-b border-border">
                                    <div className="font-medium text-lg">{fmtDate(d.date)}</div>
                                    <div className="flex gap-5 text-sm">
                                        <div>
                                            <span className="text-muted-foreground">{t("orders_count")}: </span>
                                            <span className="font-medium tabular-nums">{d.orders}</span>
                                        </div>
                                        {d.discount > 0 && (
                                            <div>
                                                <span className="text-muted-foreground">{t("discount")}: </span>
                                                <span className="font-medium text-emerald-400 tabular-nums">− {fmtSDG(d.discount, lang)}</span>
                                            </div>
                                        )}
                                        <div>
                                            <span className="text-muted-foreground">{t("revenue")}: </span>
                                            <span className="font-medium text-primary tabular-nums">{fmtSDG(d.total, lang)}</span>
                                        </div>
                                    </div>
                                </div>
                                {d.items.length === 0 ? (
                                    <div className="text-sm text-muted-foreground">—</div>
                                ) : (
                                    <div className="space-y-1.5">
                                        {d.items.map((it, i) => (
                                            <div key={i} className="flex items-center justify-between text-sm" data-testid={`day-item-${i}`}>
                                                <div className="flex items-center gap-3">
                                                    <span className="w-6 h-6 rounded-full bg-secondary text-xs flex items-center justify-center tabular-nums">{it.qty}</span>
                                                    <span>{lang === "ar" ? it.name_ar : it.name_en}</span>
                                                </div>
                                                <span className="text-primary font-medium tabular-nums">{fmtSDG(it.revenue, lang)}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}

            {/* Audit log: deleted items */}
            <Card className="grain bg-card/70" data-testid="audit-log-card">
                <CardContent className="p-5">
                    <div className="flex items-center gap-2 mb-3">
                        <Trash2 className="h-5 w-5 text-destructive" />
                        <h2 className="font-medium text-lg">{t("deleted_items_log")}</h2>
                        <span className="text-xs text-muted-foreground ms-auto">{audit?.events?.length || 0}</span>
                    </div>
                    {!audit || audit.events.length === 0 ? (
                        <div className="text-sm text-muted-foreground py-3 text-center">{t("no_deletions")}</div>
                    ) : (
                        <div className="space-y-2">
                            {audit.events.map((e) => (
                                <div key={e.id} className="flex flex-wrap items-center justify-between gap-2 bg-secondary/40 rounded-lg px-3 py-2 text-sm" data-testid={`audit-row-${e.id}`}>
                                    <div className="flex items-center gap-2 flex-1 min-w-0">
                                        <span className="w-6 h-6 rounded-full bg-destructive/15 text-destructive text-xs flex items-center justify-center tabular-nums">{e.qty}×</span>
                                        <span className="truncate">{lang === "ar" ? e.item_name_ar : e.item_name_en}</span>
                                        <span className="text-xs text-muted-foreground">· #{e.order_no}</span>
                                    </div>
                                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                        <span>{e.removed_by_name} ({t(e.removed_by_role)})</span>
                                        <span>· {fmtTime(e.at)}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
